import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import CustomDropdown from '../../Components/Shared/CustomDropdown';
import { getOfferings } from '../../api/offerings';
import '../AdventureShared.css';
import './MarketplacePage.css';

export default function MarketplacePage() {
  const navigate = useNavigate();

  const [offerings, setOfferings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter & Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [deliveryFilter, setDeliveryFilter] = useState('all');
  const [sortOption, setSortOption] = useState('newest');

  const fetchPublicOfferings = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = {
        status: 'active',
        search: searchQuery,
        sort: sortOption
      };
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }

      const res = await getOfferings(params);
      let list = res.data || [];

      // Additional client side filtering for delivery type if specified
      if (deliveryFilter !== 'all') {
        list = list.filter(o => o.deliveryType === deliveryFilter || o.deliveryType === 'both');
      }

      setOfferings(list);
    } catch (err) {
      console.error("Failed to load marketplace offerings:", err);
      setError("Failed to load marketplace listings. Please check your network connection.");
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedCategory, deliveryFilter, sortOption]);

  useEffect(() => { fetchPublicOfferings(); }, [fetchPublicOfferings]);

  const formatPrice = (offering) => {
    const { pricingModel, price, currency } = offering;
    if (pricingModel === 'free') return 'Free';
    if (pricingModel === 'cod') return 'Cash on Delivery';

    let cleanCurrency = (currency || 'USD').trim().toUpperCase();
    if (cleanCurrency === 'RS') cleanCurrency = 'INR';
    if (!/^[A-Z]{3}$/.test(cleanCurrency)) cleanCurrency = 'USD';

    let formatted;
    try {
      formatted = new Intl.NumberFormat(undefined, {
        style: 'currency', currency: cleanCurrency
      }).format(price || 0);
    } catch (e) {
      formatted = `${cleanCurrency} ${(price || 0).toFixed(2)}`;
    }

    const modelSuffix = pricingModel === 'per_hour' ? '/hr' : pricingModel === 'per_day' ? '/day' : '';
    return `${formatted}${modelSuffix}`;
  };

  const CATEGORIES = [
    { id: 'all', label: 'All Offerings' },
    { id: 'service', label: '🛠️ Services' },
    { id: 'event', label: '🎟️ Events' },
    { id: 'product', label: '📦 Products' },
  ];

  const currentUserId = localStorage.getItem("userId") || "";

  return (
    <div className="MarketplacePage">
      <div className="adventurePageContainer">
        <div className="headerContain"><Header /></div>

        <div className="adventurePageContainer2">
          <div className="sidebarContain"><Sidebar /></div>

          <main className="adv-page-main">
            {/* Hero Header */}
            <div className="mp-hero-banner">
              <div className="mp-hero-content">
                <h1 className="mp-hero-title">Discover Services & <span className="adv-page-title-accent">Offerings</span></h1>
                <p className="mp-hero-sub">Browse, request, and book verified experiences, repairs, consultations, and products.</p>

                {/* Search Input in Hero */}
                <div className="mp-search-bar">
                  <span className="mp-search-icon">🔍</span>
                  <input
                    type="text"
                    className="mp-search-input"
                    placeholder="Search by title, service type, guide, or keywords..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button className="mp-search-clear" onClick={() => setSearchQuery('')}>×</button>
                  )}
                </div>
              </div>
            </div>

            {/* Category Navigation Pills */}
            <div className="mp-category-pills">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  className={`mp-category-pill ${selectedCategory === cat.id ? 'mp-category-pill--active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Controls Row: Secondary Filters & Sort */}
            <div className="mp-controls-row">
              <div className="mp-filter-group">
                <label className="mp-filter-label">Delivery:</label>
                <CustomDropdown
                  value={deliveryFilter}
                  onChange={setDeliveryFilter}
                  options={[
                    { value: 'all',       label: 'All Types'       },
                    { value: 'in_person', label: 'In-Person'       },
                    { value: 'virtual',   label: 'Virtual / Online' },
                  ]}
                />
              </div>

              <div className="mp-filter-group">
                <label className="mp-filter-label">Sort By:</label>
                <CustomDropdown
                  value={sortOption}
                  onChange={setSortOption}
                  options={[
                    { value: 'newest',     label: 'Newest First'       },
                    { value: 'oldest',     label: 'Oldest First'       },
                    { value: 'price_asc',  label: 'Price: Low to High' },
                    { value: 'price_desc', label: 'Price: High to Low' },
                  ]}
                />
              </div>
            </div>

            {/* Grid of Public Offering Cards */}
            <div className="mp-grid-container">
              {isLoading ? (
                <div className="adv-state-text" style={{ gridColumn: '1 / -1', padding: '60px 0' }}>
                  Loading marketplace offerings...
                </div>
              ) : error ? (
                <div className="adv-state-error" style={{ gridColumn: '1 / -1' }}>
                  {error}
                </div>
              ) : offerings.length === 0 ? (
                <div className="adv-empty" style={{ gridColumn: '1 / -1', padding: '60px 20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
                  <h3>No matching offerings found</h3>
                  <p>Try adjusting your search keywords or clearing filter preferences.</p>
                  <button className="adv-btn-outline" style={{ marginTop: '16px' }} onClick={() => { setSearchQuery(''); setSelectedCategory('all'); setDeliveryFilter('all'); }}>
                    Reset All Filters
                  </button>
                </div>
              ) : (
                offerings.map(offering => {
                  const isOwner = (offering.providerId || offering.provider?.providerId || offering.provider?.userId) === currentUserId;

                  return (
                    <div
                      key={offering.offeringId}
                      className="mp-card"
                      style={isOwner ? { borderColor: 'rgba(72, 209, 204, 0.4)', backgroundColor: 'rgba(14, 27, 27, 0.9)' } : {}}
                      onClick={() => navigate(`/facilitate/offerings/${offering.offeringId}`, { state: { fromMarketplace: true } })}
                    >
                      <div className="mp-card__thumbnail-wrap">
                        <img
                          src={offering.coverImageUrl || "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=600&q=80"}
                          alt={offering.title}
                          className="mp-card__img"
                        />
                        {isOwner ? (
                          <span className="mp-card__category-badge" style={{ backgroundColor: '#48d1cc', color: '#071414' }}>
                            👑 Your Offering
                          </span>
                        ) : (
                          <span className="mp-card__category-badge">{offering.category}</span>
                        )}
                        {offering.category?.toLowerCase() === 'product' ? (
                          <span className="mp-card__delivery-badge">
                            {offering.subcategory?.toLowerCase() === 'digital' ? '📥 Digital' : '📦 Shipped'}
                          </span>
                        ) : offering.deliveryType && (
                          <span className="mp-card__delivery-badge">
                            {offering.deliveryType === 'virtual' ? '💻 Virtual' : offering.deliveryType === 'in_person' ? '📍 In-Person' : '🌐 Both'}
                          </span>
                        )}
                      </div>

                      <div className="mp-card__content">
                        <h3 className="mp-card__title">{offering.title}</h3>
                        <p className="mp-card__snippet">{offering.description}</p>

                        <div className="mp-card__footer">
                          <div className="mp-card__provider">
                            <img
                              src={offering.provider?.avatarUrl || "/profilePic.jpg"}
                              alt={offering.provider?.name || "Provider"}
                              className="mp-card__avatar"
                            />
                            <span className="mp-card__provider-name">
                              {isOwner ? "You (Owner)" : (offering.provider?.name || "Verified Provider")}
                            </span>
                          </div>

                          <div className="mp-card__price-wrap">
                            <span className="mp-card__price">{formatPrice(offering)}</span>
                          </div>
                        </div>

                        {isOwner ? (
                          <button className="mp-card__btn-book" style={{ backgroundColor: 'transparent', border: '1.5px solid #48d1cc', color: '#48d1cc' }}>
                            Manage Offering ⚙️
                          </button>
                        ) : (
                          <button className="mp-card__btn-book">
                            {offering.category?.toLowerCase() === 'product' ? 'View & Buy \u2192' : 'View & Book \u2192'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
